import { onUnmounted, ref } from 'vue';

/**
 * Page-wide file drag-and-drop detection, scoped to the lifetime of the
 * component that calls it. While a `<GaiaFileDropper>` is mounted, files can be
 * dropped anywhere on the page; when it unmounts the listeners are removed.
 *
 * This is a de-coupled refactor of the old `useGlobalDragDrop` composable: the
 * event handling is unchanged, but there is no hard-coded page allow-list and
 * no built-in action — the caller decides what happens on drop via `onDrop`.
 */
export function useFileDrop(onDrop: (files: File[]) => void): {
  isDragging: ReturnType<typeof ref<boolean>>;
  reset: () => void;
} {
  const isDragging = ref(false);

  let dragCounter = 0;
  let dragTimeout: ReturnType<typeof setTimeout> | null = null;

  // Decide whether the global dropper should handle this drag, or yield to a
  // more specific target (an upload dragger, or any open modal/drawer/popover).
  const isDragDropAllowed = (e?: DragEvent): boolean => {
    if (e?.dataTransfer) {
      const hasFiles = e.dataTransfer.types.includes('Files');
      const hasText = e.dataTransfer.types.includes('text/plain');

      if (hasFiles) {
        // Let a Naive upload dragger handle drops aimed directly at it.
        return !(e.target instanceof Element && e.target.closest('.n-upload-dragger'));
      }

      // Text but no files (e.g. a kanban ticket drag) — not our concern.
      if (hasText) {
        return false;
      }
    }

    // Yield while another overlay is open.
    const existingOverlays = document.querySelectorAll(
      [
        '.n-modal-mask', // Naive UI modals
        '.n-drawer-mask', // Naive UI drawers
        '.n-popover', // Naive UI popovers
        '.n-dropdown-menu', // Naive UI dropdowns
        '[role="dialog"]', // Generic dialogs
      ].join(', ')
    );

    return existingOverlays.length === 0;
  };

  const reset = (): void => {
    isDragging.value = false;
    dragCounter = 0;
    if (dragTimeout) {
      clearTimeout(dragTimeout);
      dragTimeout = null;
    }
  };

  const handleDragEnter = (e: DragEvent): void => {
    if (!isDragDropAllowed(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    dragCounter++;

    if (dragTimeout) {
      clearTimeout(dragTimeout);
      dragTimeout = null;
    }

    if (e.dataTransfer?.types.includes('Files')) {
      isDragging.value = true;

      // Safety net — reset the drag state if something swallows the drop event.
      dragTimeout = setTimeout(() => {
        reset();
      }, 5000);
    }
  };

  const handleDragLeave = (e: DragEvent): void => {
    if (!isDragging.value) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    dragCounter--;
    if (dragCounter === 0) {
      reset();
    }
  };

  const handleDragOver = (e: DragEvent): void => {
    if (!isDragDropAllowed(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e: DragEvent): void => {
    if (!isDragDropAllowed(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    reset();

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      onDrop(Array.from(files));
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('dragenter', handleDragEnter, true);
    document.addEventListener('dragleave', handleDragLeave, true);
    document.addEventListener('dragover', handleDragOver, true);
    document.addEventListener('drop', handleDrop, true);
  }

  onUnmounted(() => {
    document.removeEventListener('dragenter', handleDragEnter, true);
    document.removeEventListener('dragleave', handleDragLeave, true);
    document.removeEventListener('dragover', handleDragOver, true);
    document.removeEventListener('drop', handleDrop, true);
  });

  return { isDragging, reset };
}
